import { type ThreeEvent, useThree } from '@react-three/fiber';
import { VisibleScans } from '@viewer3D/types';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Group, Vector3 } from 'three';
import { Scan } from '../Scan';
import { useConnect } from './connect';

const TransformableScanBase = ({
  scanId,
  onOver,
  onClick,
  visibleScans,
  isScanChecked,
}: {
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
  onOver?: (event: ThreeEvent<MouseEvent>) => void;
  scanId: string;
  visibleScans: VisibleScans;
  isScanChecked: boolean;
}) => {
  const { scan, onScanLoadedToScene, userTransformation, isSelected } = useConnect(scanId);
  const invalidate = useThree((state) => state.invalidate);
  const centeredScanRef = useRef<Group>(null);
  const [geometryCenterCorrection, setGeometryCenterCorrection] = useState<Vector3>();

  const scanLoadedHandler = useCallback(
    (geoCenterCorrection: Vector3) => {
      setGeometryCenterCorrection(geoCenterCorrection);
      onScanLoadedToScene();
      invalidate();
    },
    [invalidate, onScanLoadedToScene],
  );

  const { position, rotation, helperColor } = userTransformation;
  const posX = position.x;
  const posY = position.y;
  const posZ = position.z;
  const rotX = rotation.x;
  const rotY = rotation.y;
  const rotZ = rotation.z;

  // Hook 1: Positioning and rotating the mesh in 3D space with stable primitive coordinates
  useEffect(() => {
    const { current: centeredScan } = centeredScanRef;
    if (!centeredScan || !geometryCenterCorrection) return;
    centeredScan.position
      .set(0, 0, 0)
      .copy(position)
      // we apply the userRotation, to make the translations saved in the db usable for the frontend transformations
      .sub(geometryCenterCorrection.clone().applyEuler(rotation));
    centeredScan.rotation.set(0, 0, 0).copy(rotation);
    invalidate();
  }, [centeredScanRef, posX, posY, posZ, rotX, rotY, rotZ, geometryCenterCorrection, invalidate]);

  if (
    !scan ||
    !scan.selectedProcessingResultId ||
    visibleScans === 'NONE' ||
    (visibleScans === 'ONLY_VISIBLE' && !scan.visible)
  )
    return null;

  return (
    <Scan
      isSelected={isSelected || isScanChecked}
      helperColor={helperColor}
      ref={centeredScanRef}
      scanId={scanId}
      processingResultId={scan.selectedProcessingResultId}
      onLoaded={scanLoadedHandler}
      onClick={onClick}
      onOver={onOver}
    />
  );
};

export const TransformableScan = memo(TransformableScanBase);
